/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { LinkButton, Text } from '@blend/enterprise-components';

import { FormCardWrapper, FormCardContent } from 'blb-components/FormCard';
import { cookies } from 'blb-utils/cookies';
import { getConfig } from 'blb-utils/config';
import { getFormattedDisplay } from 'blb-utils/date';
import { useChallengeCookie } from 'blb-utils/login';
import { resendEmailMFAChallenge } from 'blb-api/auth';

export namespace EmailMFAPage {
  export interface Props {}
}

export const EmailMFAPage: React.FC<EmailMFAPage.Props> = () => {
  const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState<boolean>(false);
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [lastResendMessage, setLastResendMessage] = useState<string>();
  const challengeCookieResult = useChallengeCookie();

  // If user does not click the magic link before 5 minutes is up, redirect back
  // to login page.
  useEffect(() => {
    const timeout = setTimeout(() => setShouldRedirectToLogin(true), getConfig().sessionLimits.emailMFALoginWindow);

    return () => clearTimeout(timeout);
  }, []);

  const notReceivedString = `Didn't receive it? `;
  const resendEmail = async () => {
    setIsResendingEmail(true);
    try {
      await resendEmailMFAChallenge();
      setLastResendMessage(`Last email sent on ${getFormattedDisplay(Date.now(), 'hh:mma')}. ${notReceivedString}`);
      setIsResendingEmail(false);
    } catch (e) {
      setShouldRedirectToLogin(true);
    }
  };

  if (shouldRedirectToLogin) {
    cookies.remove('challenge_cookie');
    return (
      <Redirect
        to={`/login?error=${encodeURIComponent(
          'There was an error resending your login email. Please try logging in again.',
        )}`}
      />
    );
  }

  return (
    <FormCardWrapper>
      <FormCardContent
        formattedId="it-EmailMFA-FormCard"
        headerLabel={
          <Text>
            Blend has emailed a link to <Text weight="bold">{challengeCookieResult.cookie?.username}</Text>. Please use
            the link to log in.
          </Text>
        }
        headerTitle="Email verification"
      >
        <Text data-it="EmailMFA-FormCard-innerMessage">
          {lastResendMessage} {!lastResendMessage && notReceivedString}
          <LinkButton
            btnSize="sm"
            disabled={isResendingEmail}
            id="it-EmailMFA"
            inline
            label={isResendingEmail ? 'Sending...' : 'Resend email.'}
            onClick={resendEmail}
          />
        </Text>
      </FormCardContent>
    </FormCardWrapper>
  );
};
